// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// ** utils imports
import Transition from '../../../utils/Transition';
import { formatUtcToLocal } from '../../../utils/Utils';

const QuizTableItem = props => {
  // props
  const { data, isSelected } = props;

  // states for dropdown menu
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close dropdown on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close dropdown on escape key
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Get status badge with violet theme
  const getStatusBadge = (isActive, isDeleted) => {
    if (isDeleted) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Deleted
        </span>
      );
    }
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
        Inactive
      </span>
    );
  };

  return (
    <tr
      className={`${
        isSelected ? 'bg-violet-50 dark:bg-violet-900/20' : ''
      } hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors`}
    >
      {/* Template Name */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-violet-600 dark:text-violet-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="font-medium text-gray-800 dark:text-gray-100">
            {data?.templateName || 'N/A'}
          </div>
        </div>
      </td>

      {/* Form Version */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          v{data?.formVersion || '1.0'}
        </span>
      </td>

      {/* Total Sections */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <span className="text-gray-800 dark:text-gray-100 font-medium">
            {data?.totalSections || 0}
          </span>
          <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">
            sections
          </span>
        </div>
      </td>

      {/* Description */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div
          className="text-gray-800 dark:text-gray-100 max-w-xs truncate"
          title={data?.description}
        >
          {data?.description || 'No description'}
        </div>
      </td>

      {/* Status */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        {getStatusBadge(data?.isActive, data?.isDeleted)}
      </td>

      {/* Created Date */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-gray-800 dark:text-gray-100">
          {formatUtcToLocal(data?.createdAt)}
        </div>
      </td>

      {/* Actions */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
        <div className="relative inline-flex">
          <button
            ref={trigger}
            className="text-gray-400 hover:text-violet-600 dark:text-gray-500 dark:hover:text-violet-400 rounded-full transition-colors"
            aria-haspopup="true"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-expanded={dropdownOpen}
          >
            <span className="sr-only">Menu</span>
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="2" />
              <circle cx="10" cy="16" r="2" />
              <circle cx="22" cy="16" r="2" />
            </svg>
          </button>
          <Transition
            show={dropdownOpen}
            tag="div"
            className="origin-top-right z-10 absolute top-full right-0 min-w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1"
            enter="transition ease-out duration-200 transform"
            enterStart="opacity-0 -translate-y-2"
            enterEnd="opacity-100 translate-y-0"
            leave="transition ease-out duration-200"
            leaveStart="opacity-100"
            leaveEnd="opacity-0"
          >
            <div ref={dropdown}>
              <Link
                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 flex items-center py-1 px-3 transition-colors uppercase"
                to={`/quiz/${data?._id}`}
                onClick={() => setDropdownOpen(false)}
              >
                <svg
                  className="w-4 h-4 fill-current text-gray-400 dark:text-gray-500 shrink-0 mr-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 0C5.829 0 4 1.829 4 4s1.829 4 4 4 4-1.829 4-4-1.829-4-4-4zM8 6c-1.103 0-2-.897-2-2s.897-2 2-2 2 .897 2 2-.897 2-2 2zM9 7H7a7 7 0 00-7 7v2h16v-2a7 7 0 00-7-7z" />
                </svg>
                <span>View Details</span>
              </Link>
              <Link
                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 flex items-center py-1 px-3 transition-colors uppercase"
                to={`/quiz/edit/${data?._id}`}
                onClick={() => setDropdownOpen(false)}
              >
                <svg
                  className="w-4 h-4 fill-current text-gray-400 dark:text-gray-500 shrink-0 mr-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                </svg>
                <span>Edit Template</span>
              </Link>
            </div>
          </Transition>
        </div>
      </td>
    </tr>
  );
};

QuizTableItem.propTypes = {
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
};

export default QuizTableItem;
